import React from 'react';
import {
  createPluginFactory,
  insertTableColumn,
  insertTableRow,
  onKeyDownTable,
  ELEMENT_TABLE,
  ELEMENT_TH,
  ELEMENT_TR,
  ELEMENT_TD,
  getTableCellBorders,
  findNode,
} from '@udecode/plate';

import { withTable } from './withTable';
import { translateBorderStyle } from './PlateTableCellElement';

/**
 * Enables support for tables.
 */
export const createTablePlugin = createPluginFactory({
  key: ELEMENT_TABLE,
  isElement: true,
  handlers: {
    onKeyDown: onKeyDownTable,
  },
  deserializeHtml: {
    rules: [{ validNodeName: 'TABLE' }],
  },
  serializeHtml: ({ attributes, children, element }) => {
    const { colSizes, marginLeft } = element;
    const style = marginLeft ? { marginLeft } : {};

    return (
      <table
        {...attributes}
        className="slate-table"
        style={style}
      >
        {colSizes?.length > 0 && (
          <colgroup>
            {colSizes.map(size => (
              <col
                style={{
                  ... size > 0 && { width: `${size}px` },
                  minWidth: '48px',
                }}
              />
            ))}
            {
              /* Need to make sure that all col sizes are non zero before adding this
              last colgroup. Otherwise the 0 sized columns will actually be zero width when rendered. */
            }
            {colSizes.every(col => col > 0) && (
              <col
                style={{ width: '100%' }}
              />
            )}
          </colgroup>
        )}
        <tbody>
          {children}
        </tbody>
      </table>
    );
  },
  options: {
    insertColumn: (e, { fromCell }) => {
      insertTableColumn(e, {
        fromCell,
        disableSelect: true,
      });
    },
    insertRow: (e, { fromRow }) => {
      insertTableRow(e, {
        fromRow,
        disableSelect: true,
      });
    },
    minColumnWidth: 48,
  },
  withOverrides: withTable,
  plugins: [
    {
      key: ELEMENT_TR,
      isElement: true,
      deserializeHtml: {
        rules: [{ validNodeName: 'TR' }],
      },
      serializeHtml: ({ attributes, children, element }) => {
        const { size } = element;
        const style = { minHeight: size };
        return (
          <tr
            {...attributes}
            style={style}
            className="slate-tr"
          >
            {children}
          </tr>
        );
      },
    },
    {
      key: ELEMENT_TD,
      isElement: true,
      deserializeHtml: {
        attributeNames: ['rowspan', 'colspan'],
        rules: [{ validNodeName: 'TD' }],
        getNode: (element) => {
          const background = element.style.background || element.style.backgroundColor;
          const colspan = element.getAttribute('colspan');
          return {
            type: 'td',
            ... background && { bgColor: background },
            ... colspan && { colSpan: colspan },
          };
        },
      },
      serializeHtml: ({ attributes, children, element, editor }) => {
        const noBorderStyle = '0px solid transparent';
        const { bgColor, colSpan } = element;

        const found = findNode(editor, { match: { id: element.id }, at: [] });
        const [, path] = found || [];
        const [rowIndex, colIndex] = path.length > 1 ? path.slice(-2) : [];
        const isFirstCell = colIndex === 0;
        const isFirstRow = rowIndex === 0;
        const borders = getTableCellBorders(element, { isFirstCell, isFirstRow });
        const {
          bottom,
          left,
          right,
          top,
        } = borders || {};

        const style = {
          backgroundColor: bgColor || 'transparent',
          borderTop: top ? translateBorderStyle(top) : noBorderStyle,
          borderRight: right ? translateBorderStyle(right) : noBorderStyle,
          borderBottom: bottom ? translateBorderStyle(bottom) : noBorderStyle,
          borderLeft: left ? translateBorderStyle(left) : noBorderStyle,
        };
        return (
          <td
            {...attributes}
            style={style}
            className="slate-td"
            colSpan={colSpan}
          >
            <div>
              {children}
            </div>
          </td>
        );
      },
      props: ({ element }) => ({
        nodeProps: {
          colSpan: (element?.attributes)?.colspan,
          rowSpan: (element?.attributes)?.rowspan,
        },
      }),
    },
    {
      key: ELEMENT_TH,
      isElement: true,
      deserializeHtml: {
        attributeNames: ['rowspan', 'colspan'],
        rules: [{ validNodeName: 'TH' }],
        getNode: (element) => {
          const background = element.style.background || element.style.backgroundColor;
          const colspan = element.getAttribute('colspan');
          return {
            type: 'th',
            ... background && { bgColor: background },
            ... colspan && { colSpan: colspan },
          };
        },
      },
      serializeHtml: ({ attributes, children, element, editor }) => {
        // #TODO: This was copy/pasted from the td serializeHtml. Could refactor so they are the same.
        const noBorderStyle = '0px solid transparent';
        const { bgColor, colSpan } = element;

        const found = findNode(editor, { match: { id: element.id }, at: [] });
        const [, path] = found || [];
        const [rowIndex, colIndex] = path.length > 1 ? path.slice(-2) : [];
        const isFirstCell = colIndex === 0;
        const isFirstRow = rowIndex === 0;
        const borders = getTableCellBorders(element, { isFirstCell, isFirstRow });
        const {
          bottom,
          left,
          right,
          top,
        } = borders || {};

        const style = {
          backgroundColor: bgColor || 'transparent',
          borderTop: top ? translateBorderStyle(top) : noBorderStyle,
          borderRight: right ? translateBorderStyle(right) : noBorderStyle,
          borderBottom: bottom ? translateBorderStyle(bottom) : noBorderStyle,
          borderLeft: left ? translateBorderStyle(left) : noBorderStyle,
        };
        return (
          <th
            {...attributes}
            style={style}
            className="slate-td"
            colSpan={colSpan}
          >
            <div>
              {children}
            </div>
          </th>
        );
      },
      props: ({ element }) => ({
        nodeProps: {
          colSpan: (element?.attributes)?.colspan,
          rowSpan: (element?.attributes)?.rowspan,
        },
      }),
    },
  ],
});
